import { useEffect, useState } from 'react';
import { Card, Button, Spinner, FormField } from '../components/ui';

const STATUS_COLORS = {
  pending:'#d97706', viewed:'#3b82f6', quoted:'#8b5cf6',
  accepted:'#16a34a', declined:'#dc2626', completed:'#16a34a',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

const styles = `
  body { background: #f5f2ec; font-family: 'DM Sans', sans-serif; }
  .cd-wrap { max-width: 900px; margin: 0 auto; padding: 40px 20px; }
  .cd-header { background: #1a1a18; color: #fff; padding: 20px 32px; border-radius: 12px; margin-bottom: 32px; display: flex; justify-content: space-between; align-items: center; }
  .cd-logo { font-size: 20px; font-weight: 300; }
  .cd-logo em { color: #6ee7b7; font-style: italic; }
`;

export default function ContractorDashboard() {
  const [profile, setProfile]   = useState(null);
  const [quotes, setQuotes]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('quotes');
  const [respondingID, setRespondingID] = useState(null);
  const [response, setResponse] = useState({ status:'quoted', contractor_notes:'', quoted_amount_gbp:'' });
  const [saving, setSaving]     = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});

  const token = () => localStorage.getItem('contractor_token');
  const h = () => ({ 'Content-Type':'application/json', Authorization:`Bearer ${token()}` });

  useEffect(() => {
    if (!token()) { window.location.href = '/contractor/signup'; return; }
    Promise.all([
      fetch('/api/contractor/profile', { headers: h() }).then(r => r.json()),
      fetch('/api/contractor/quotes',  { headers: h() }).then(r => r.json()),
    ]).then(([p, q]) => {
      setProfile(p);
      setProfileForm({
        full_name: p.full_name, company_name: p.company_name || '',
        phone: p.phone || '', postcode: p.postcode || '',
        coverage_radius_miles: p.coverage_radius_miles || 25, bio: p.bio || '', website: p.website || '',
      });
      setQuotes(Array.isArray(q) ? q : []);
      setLoading(false);
    });
  }, []);

  async function handleRespond() {
    setSaving(true);
    try {
      const body = {
        status: response.status,
        contractor_notes: response.contractor_notes || null,
        quoted_amount_gbp: response.quoted_amount_gbp
          ? Math.round(parseFloat(response.quoted_amount_gbp) * 100) : null,
      };
      await fetch(`/api/contractor/quotes/${respondingID}`, { method:'PUT', headers: h(), body: JSON.stringify(body) });
      setQuotes(qs => qs.map(q => q.id === respondingID ? { ...q, ...body } : q));
      setRespondingID(null);
    } finally { setSaving(false); }
  }

  async function saveProfile() {
    setSaving(true);
    try {
      await fetch('/api/contractor/profile', {
        method:'PUT', headers: h(),
        body: JSON.stringify({ ...profileForm, coverage_radius_miles: Number(profileForm.coverage_radius_miles) }),
      });
      setProfile(p => ({ ...p, ...profileForm }));
      setEditProfile(false);
    } finally { setSaving(false); }
  }

  function logout() {
    localStorage.removeItem('contractor_token');
    localStorage.removeItem('contractor');
    window.location.href = '/contractor/signup';
  }

  const set = k => e => setResponse(r => ({ ...r, [k]: e.target.value }));
  const setP = k => e => setProfileForm(f => ({ ...f, [k]: e.target.value }));

  const pending  = quotes.filter(q => q.status === 'pending');
  const active   = quotes.filter(q => ['viewed','quoted','accepted'].includes(q.status));
  const history  = quotes.filter(q => ['declined','completed'].includes(q.status));

  if (loading) return <Spinner />;

  return (
    <div>
      <style>{styles}</style>
      <div className="cd-wrap">
        {/* Header */}
        <div className="cd-header">
          <div className="cd-logo">Landlord<em>Comply</em> — Contractor Portal</div>
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <span style={{ fontSize:14, color:'rgba(255,255,255,.6)' }}>{profile?.full_name}</span>
            {profile?.sub_status === 'active' || profile?.sub_status === 'trialing'
              ? <span style={{ fontSize:12, background:'#dcfce7', color:'#15803d', padding:'3px 10px', borderRadius:12, fontWeight:600 }}>● Active</span>
              : <span style={{ fontSize:12, background:'#fee2e2', color:'#dc2626', padding:'3px 10px', borderRadius:12, fontWeight:600 }}>Inactive</span>
            }
            <button onClick={logout} style={{ background:'none', border:'none', color:'rgba(255,255,255,.5)', cursor:'pointer', fontSize:13 }}>Sign out</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:24 }}>
          {[
            { key:'quotes', label:`📋 Quote requests (${pending.length + active.length})` },
            { key:'profile', label:'👤 My profile' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding:'8px 20px', borderRadius:20, border:'1.5px solid #e5e7eb',
              background: tab === t.key ? '#1a1a18' : 'transparent',
              color: tab === t.key ? '#fff' : '#1a1a18',
              fontSize:14, cursor:'pointer', fontWeight:500,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'quotes' && (<>
          {pending.length > 0 && (
            <div style={{ marginBottom:32 }}>
              <h3 style={{ fontSize:16, marginBottom:12, color:'#d97706' }}>⚡ New requests — {pending.length}</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {pending.map(q => <QuoteCard key={q.id} q={q} onRespond={() => { setRespondingID(q.id); setResponse({ status:'quoted', contractor_notes:'', quoted_amount_gbp:'' }); }} />)}
              </div>
            </div>
          )}

          {active.length > 0 && (
            <div style={{ marginBottom:32 }}>
              <h3 style={{ fontSize:16, marginBottom:12 }}>Active quotes</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {active.map(q => <QuoteCard key={q.id} q={q} onRespond={() => { setRespondingID(q.id); setResponse({ status: q.status, contractor_notes: q.contractor_notes || '', quoted_amount_gbp: q.quoted_amount_gbp ? String(q.quoted_amount_gbp/100) : '' }); }} />)}
              </div>
            </div>
          )}

          {quotes.length === 0 && (
            <Card style={{ textAlign:'center', padding:48 }}>
              <p style={{ fontSize:32, marginBottom:12 }}>📬</p>
              <p style={{ fontWeight:600, marginBottom:8 }}>No quote requests yet</p>
              <p style={{ color:'#6b7280', fontSize:14 }}>When landlords request quotes, they'll appear here. Make sure your profile is complete to attract more leads.</p>
            </Card>
          )}
        </>)}

        {tab === 'profile' && (
          <Card style={{ padding:32 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ fontSize:20 }}>Your profile</h3>
              <Button size="sm" variant="ghost" onClick={() => setEditProfile(e => !e)}>
                {editProfile ? 'Cancel' : 'Edit profile'}
              </Button>
            </div>

            {!editProfile ? (
              <div style={{ display:'grid', gap:16 }}>
                {[
                  ['Name', profile?.full_name],
                  ['Company', profile?.company_name || '—'],
                  ['Phone', profile?.phone || '—'],
                  ['Postcode', profile?.postcode || '—'],
                  ['Coverage', `${profile?.coverage_radius_miles} miles`],
                  ['Website', profile?.website || '—'],
                ].map(([l, v]) => (
                  <div key={l} style={{ display:'flex', gap:16 }}>
                    <p style={{ width:120, fontSize:13, color:'#6b7280', flexShrink:0 }}>{l}</p>
                    <p style={{ fontSize:14 }}>{v}</p>
                  </div>
                ))}
                {profile?.bio && (
                  <div style={{ display:'flex', gap:16 }}>
                    <p style={{ width:120, fontSize:13, color:'#6b7280', flexShrink:0 }}>About</p>
                    <p style={{ fontSize:14, lineHeight:1.5 }}>{profile.bio}</p>
                  </div>
                )}
                <div>
                  <p style={{ fontSize:13, color:'#6b7280', marginBottom:8 }}>Trades</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {profile?.trades?.map(t => (
                      <span key={t.id} style={{ background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:6, padding:'4px 12px', fontSize:13, fontWeight:500 }}>
                        {t.trade_label} {t.accreditation ? `· ${t.accreditation}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  <FormField label="Full name"><input value={profileForm.full_name} onChange={setP('full_name')} /></FormField>
                  <FormField label="Company name"><input value={profileForm.company_name} onChange={setP('company_name')} /></FormField>
                  <FormField label="Phone"><input value={profileForm.phone} onChange={setP('phone')} /></FormField>
                  <FormField label="Postcode"><input value={profileForm.postcode} onChange={setP('postcode')} /></FormField>
                  <FormField label="Coverage (miles)">
                    <select value={profileForm.coverage_radius_miles} onChange={setP('coverage_radius_miles')}>
                      {[10,15,20,25,30,40,50].map(m => <option key={m} value={m}>{m} miles</option>)}
                    </select>
                  </FormField>
                  <FormField label="Website"><input value={profileForm.website} onChange={setP('website')} placeholder="https://…" /></FormField>
                </div>
                <FormField label="About you">
                  <textarea value={profileForm.bio} onChange={setP('bio')} rows={4} />
                </FormField>
                <div style={{ display:'flex', justifyContent:'flex-end', gap:12, marginTop:8 }}>
                  <Button variant="ghost" onClick={() => setEditProfile(false)}>Cancel</Button>
                  <Button loading={saving} onClick={saveProfile}>Save changes</Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Respond modal */}
      {respondingID && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setRespondingID(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:480, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:20 }}>Respond to quote request</h3>

            <FormField label="Your response">
              <select value={response.status} onChange={set('status')}>
                <option value="quoted">Send a quote</option>
                <option value="viewed">Mark as viewed (not ready to quote)</option>
                <option value="declined">Decline</option>
              </select>
            </FormField>

            {response.status === 'quoted' && (
              <FormField label="Your quote (£)">
                <input type="number" step="0.01" value={response.quoted_amount_gbp}
                  onChange={set('quoted_amount_gbp')} placeholder="e.g. 120.00" />
              </FormField>
            )}

            <FormField label="Message to landlord">
              <textarea value={response.contractor_notes} onChange={set('contractor_notes')} rows={4}
                placeholder={response.status === 'declined'
                  ? "Reason for declining (optional)…"
                  : "Include your availability, what's included in the price, any questions…"} />
            </FormField>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
              <Button variant="ghost" onClick={() => setRespondingID(null)}>Cancel</Button>
              <Button loading={saving} onClick={handleRespond}>Send response</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QuoteCard({ q, onRespond }) {
  const statusColor = STATUS_COLORS[q.status] || '#6b7280';
  return (
    <Card style={{ padding:'16px 20px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap', marginBottom:6 }}>
            <p style={{ fontWeight:600 }}>{q.landlord_name}</p>
            <span style={{ fontSize:12, background:`${statusColor}20`, color:statusColor, padding:'2px 10px', borderRadius:12, fontWeight:600 }}>
              {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
            </span>
            <span style={{ fontSize:12, background:'#f3f4f6', padding:'2px 8px', borderRadius:8 }}>{q.trade_label}</span>
          </div>
          <p style={{ fontSize:14, color:'#374151', marginBottom:6, lineHeight:1.5 }}>{q.description}</p>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
            {q.property_address && <span style={{ fontSize:12, color:'#6b7280' }}>🏘 {q.property_address}</span>}
            {q.preferred_dates && <span style={{ fontSize:12, color:'#6b7280' }}>📅 {q.preferred_dates}</span>}
            <span style={{ fontSize:12, color:'#6b7280' }}>Received {formatDate(q.created_at)}</span>
            {q.landlord_email && <a href={`mailto:${q.landlord_email}`} style={{ fontSize:12, color:'#1B4332' }}>✉️ {q.landlord_email}</a>}
          </div>
        </div>
        <Button size="sm" onClick={onRespond}>
          {q.status === 'pending' ? 'Respond →' : 'Update →'}
        </Button>
      </div>
    </Card>
  );
}